import { useEffect, useReducer, useRef } from 'react';

import Alert from 'storybookConfig/stories/cells/Alert';
import { useSafeDispatch } from 'utils/hooks';

/**
 * Legacy API over the `Alert` cell, kept so its call sites did not move. New code uses `Alert`
 * directly. `kind` is 'error' | 'success' | 'warning' | 'info'; anything else is info.
 */

const KINDS = ['error', 'success', 'warning', 'info'];

// `animate` never animated (no transition was wired up); as before, it renders even when hidden.
const Message = ({ show, onClose, children, kind, animate = false, className = '' }) => {
  if (!show && !animate) return null;

  return (
    <Alert
      margin="16px 0"
      type={KINDS.includes(kind) ? kind : 'info'}
      className={className}
      onClose={onClose}
    >
      {children}
    </Alert>
  );
};

const initialState = {
  show: false,
  title: '',
  message: '',
  kind: '',
};

function messageReducer(state, { type, message, title, kind }) {
  switch (type) {
    case 'show': {
      return { show: true, message, title, kind };
    }
    case 'hide': {
      return { ...state, show: false };
    }
    default: {
      throw new Error(`Unsupported alert action type: ${type}`);
    }
  }
}

export const useMessage = (autoCloseTime) => {
  const [state, dispatch] = useReducer(messageReducer, initialState);
  const safeDispatch = useSafeDispatch(dispatch);
  const hideTimer = useRef(null);

  useEffect(() => {
    clearTimeout(hideTimer.current);
    if (state.show && autoCloseTime) {
      hideTimer.current = setTimeout(() => safeDispatch({ type: 'hide' }), autoCloseTime);
    }
  }, [state, safeDispatch, autoCloseTime]);

  const showMessage =
    (kind) =>
    ({ message, title }) =>
      safeDispatch({ type: 'show', message, title, kind });

  const showErrorMessage = showMessage('error');
  const showSuccessMessage = showMessage('success');
  const showWarningMessage = showMessage('warning');

  const hideMessage = () => safeDispatch({ type: 'hide' });

  return {
    showErrorMessage,
    showSuccessMessage,
    showWarningMessage,
    hideMessage,
    messageState: state,
  };
};

export default Message;
